import { prefersReducedMotion } from '@/lib/utils/motion';

export function isInIframe(): boolean {
  if (typeof window === 'undefined') return false;
  return window.self !== window.top;
}

export function isInSameOriginIframe(): boolean {
  if (!isInIframe()) return false;

  try {
    // Accessing parent origin throws if cross-origin
    return Boolean(window.top?.location.href);
  } catch {
    return false;
  }
}

export function isCrossOriginIframe(): boolean {
  return isInIframe() && !isInSameOriginIframe();
}

export function getScrollableParent(element: HTMLElement): HTMLElement | Window {
  let parent = element.parentElement;
  while (parent) {
    const { overflow, overflowY } = window.getComputedStyle(parent);
    if (/(auto|scroll)/.test(overflow + overflowY)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
}

/** `getScrollableParent`, with the window case resolved to the element that scrolls it. */
export function getScrollableElement(element: HTMLElement): HTMLElement | null {
  const parent = getScrollableParent(element);
  const scroller = parent instanceof Window ? document.scrollingElement : parent;
  return scroller instanceof HTMLElement ? scroller : null;
}

/**
 * Scrolls by `by` over `durationMs`, a frame at a time, for riding alongside something still
 * growing — one `scrollBy` would be clamped to the scroll that exists when it is issued.
 * Callers must hold the returned stop, or a second run fights the first.
 */
export function scrollAlongside({
  from,
  by,
  durationMs,
}: {
  from: HTMLElement;
  by: number;
  durationMs: number;
}): () => void {
  if (!durationMs || prefersReducedMotion()) {
    from.scrollTop += by;
    return () => {};
  }

  let frame = 0;
  // What this animation has moved the scroll by so far. Each frame applies only its own
  // difference, rather than writing `start + travelled` outright: content growing above the
  // card makes the browser's scroll anchoring adjust `scrollTop` between frames, and an
  // absolute write undoes that adjustment on the very next one. The two then fight — the scroll
  // steps backwards a pixel or two mid-flight, which is the stutter this is meant to avoid.
  let applied = 0;
  const began = performance.now();
  frame = requestAnimationFrame(function step(now) {
    const at = Math.min(1, (now - began) / durationMs);
    // `ease-in-out`, matching the CSS the panels open on.
    const travelled = by * (at < 0.5 ? 2 * at * at : 1 - (-2 * at + 2) ** 2 / 2);
    from.scrollTop += travelled - applied;
    applied = travelled;
    if (at < 1) frame = requestAnimationFrame(step);
  });
  return () => cancelAnimationFrame(frame);
}

/**
 * Returns the total height of sticky elements visually stacked above the
 * element. Walks up the DOM all the way to `<body>`, checking preceding
 * siblings at each level for `position: sticky`.
 */
export function getStickyOffset(element: HTMLElement): number {
  return stickyOffset(element, 'previousElementSibling');
}

/**
 * How much of the foot of the scroll area sticky elements *below* this one already hold — the
 * composer, most of all. Anything at `bottom: 0` lands under them, covered and unclickable.
 */
export function getStickyFooterOffset(element: HTMLElement): number {
  return stickyOffset(element, 'nextElementSibling');
}

/** The elements that offset holds, so a caller can watch them: a composer grows as it is
 *  typed into, and nothing else tells the surface above it. */
export function getStickyFootersBelow(element: HTMLElement): HTMLElement[] {
  return stickyElements(element, 'nextElementSibling');
}

function stickyOffset(
  element: HTMLElement,
  towards: 'previousElementSibling' | 'nextElementSibling',
): number {
  return stickyElements(element, towards).reduce((total, el) => total + el.offsetHeight, 0);
}

function stickyElements(
  element: HTMLElement,
  towards: 'previousElementSibling' | 'nextElementSibling',
): HTMLElement[] {
  const found: HTMLElement[] = [];
  let current: HTMLElement | null = element;

  while (current && current !== document.body) {
    let sibling = current[towards] as HTMLElement | null;
    while (sibling) {
      if (window.getComputedStyle(sibling).position === 'sticky') found.push(sibling);
      sibling = sibling[towards] as HTMLElement | null;
    }
    current = current.parentElement;
  }

  return found;
}

export function scrollIntoViewIfNeeded(element: HTMLElement | null) {
  if (!element) {
    return;
  }

  requestAnimationFrame(() => {
    const rect = element.getBoundingClientRect();
    const header = document.querySelector('[data-header]') as HTMLElement | null;
    // Only a header that sits *over* the scrolling content needs room left under it. The chat's is a
    // static flex sibling above it, so the scroll area already begins below it — reserving its height
    // as well landed everything that height short of its target, showing the tail of whatever came
    // before.
    const headerOverlaysContent =
      !!header && ['sticky', 'fixed'].includes(window.getComputedStyle(header).position);
    const headerHeight = headerOverlaysContent ? header.getBoundingClientRect().height : 0;
    const stickyOffset = getStickyOffset(element);
    const topThreshold = headerHeight + stickyOffset;

    // Against the surface that scrolls, not the window: a chat's viewport ends above its
    // composer, so measuring to the window's foot calls content off-screen that is not, and
    // scrolls the thread away from whatever the guest was reading.
    const port = getScrollportBounds(getScrollableElement(element));
    const isInView =
      rect.top >= port.top + topThreshold &&
      rect.bottom <= port.bottom - getStickyFooterOffset(element);

    if (!isInView) {
      element.style.scrollMarginTop = `${topThreshold}px`;
      element.scrollIntoView({ behavior: 'auto', block: 'start' });
      element.style.scrollMarginTop = '';
    }
  });
}

const CLIPPING_OVERFLOW = new Set(['clip', 'hidden', 'auto', 'scroll']);

/**
 * Where a scroller's viewport sits on screen. The document's own scroller cannot answer this
 * from its own box — that reports the whole document — so anything measuring against the foot
 * of the screen has to come through here rather than reach for `innerHeight` and be wrong
 * wherever the surface scrolls inside an element instead.
 */
export function getScrollportBounds(scroller: HTMLElement | null) {
  if (!scroller || scroller === document.scrollingElement) {
    return { top: 0, bottom: window.innerHeight };
  }
  const box = scroller.getBoundingClientRect();
  return { top: box.top, bottom: box.bottom };
}

/**
 * The horizontal band of `element` that is actually on screen, after every ancestor that clips.
 * A full-bleed box hangs outside the column it sits in, so its own edges say nothing about where
 * its edges are *seen* — which is what anything pinned to them has to measure from.
 */
export function visibleXBand(element: HTMLElement) {
  let left = -Infinity;
  let right = Infinity;
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    // Named rather than "anything but `visible`": an unresolved value is not a promise to
    // clip, and taking it as one hands back a band of whatever that element's box happens
    // to be — zero, for an element no one has laid out.
    if (!CLIPPING_OVERFLOW.has(getComputedStyle(node).overflowX)) continue;
    const box = node.getBoundingClientRect();
    left = Math.max(left, box.left);
    right = Math.min(right, box.right);
  }
  return { left, right };
}

export function scrollToElementInParent(element: HTMLElement | null, offset = 16) {
  if (!element) return;

  requestAnimationFrame(() => {
    const scrollableParent = getScrollableParent(element);

    if (!(scrollableParent instanceof HTMLElement)) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const elementRect = element.getBoundingClientRect();
    const parentRect = scrollableParent.getBoundingClientRect();
    const elementTop = elementRect.top - parentRect.top + scrollableParent.scrollTop;

    scrollableParent.scrollTo({
      top: elementTop - offset,
      behavior: 'smooth',
    });
  });
}
